/**
 * Update Notification Component
 * Displays a notification when a new app version is available
 */

'use client';

import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { UpdateNotificationProps } from '../types';
import { useAppVersion } from '../hooks';

/**
 * UI component dependencies for UpdateNotification
 */
export interface UpdateNotificationUIProps {
  /** Button component for actions */
  Button: React.ComponentType<{
    onClick?: () => void;
    variant?: 'default' | 'ghost' | 'outline' | 'secondary' | 'link' | 'destructive';
    size?: 'default' | 'sm' | 'lg' | 'icon';
    className?: string;
    children?: React.ReactNode;
  }>;
}

/**
 * Complete props including UI dependencies
 */
export interface UpdateNotificationFullProps extends UpdateNotificationProps {
  /** UI component dependencies */
  ui: UpdateNotificationUIProps;
}

/**
 * Update Notification Component
 * Automatically checks for updates and prompts the user to reload
 */
export function UpdateNotification({
  checkInterval = 5 * 60 * 1000,
  autoDismissAfter = 0,
  ui,
}: UpdateNotificationFullProps) {
  const { Button } = ui;
  const { hasUpdate, reload, currentVersion } = useAppVersion(checkInterval);
  const [dismissed, setDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (hasUpdate && !dismissed) {
      setIsVisible(true);

      // Auto-dismiss if configured
      if (autoDismissAfter > 0) {
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, autoDismissAfter);

        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, [hasUpdate, dismissed, autoDismissAfter]);

  const handleReload = () => {
    setIsVisible(false);
    reload();
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setDismissed(true);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-50 p-4 md:right-4 md:left-auto md:w-96">
      <div
        className="animate-in slide-in-from-top border-border bg-background rounded-lg border p-4 shadow-lg"
        role="alert"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 rounded-lg p-2">
            <RefreshCw className="text-primary h-5 w-5" />
          </div>

          <div className="flex-1">
            <h3 className="text-foreground mb-1 font-semibold">Update Available</h3>
            <p className="text-muted-foreground mb-3 text-sm">
              A new version of the app is available. Reload to get the latest features and fixes.
            </p>
            {currentVersion && (
              <p className="text-muted-foreground mb-3 text-xs">Version: {currentVersion}</p>
            )}

            <div className="flex gap-2">
              <Button onClick={handleReload} size="sm" className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Reload Now
              </Button>
              <Button onClick={handleDismiss} variant="ghost" size="sm">
                Later
              </Button>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss update notification"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Warning if user hasn&apos;t reloaded after a while */}
        <div className="mt-3 flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <p>Some features may not work correctly until you reload.</p>
        </div>
      </div>
    </div>
  );
}

